import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Panel } from 'primereact/panel';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { useMemo, useState } from 'react';
import PluviometrosService, {
  FilterPluviometrosType,
} from '../../services/pluviometros';
import useAsyncFetch from '../../hooks/use-async';
import { defaultColumnProps } from '../utils';
import ActionButtons from './action-buttons';
import { Pluviometro } from '../../@types/pluviometro';
import { PermissoesService, PropriedadesService } from '../../services';
import useLocalStore from '../../hooks/use-local-store';
import { Propriedade } from '../../@types';
import { convertPluviometrosToList } from './utils';

type StatusesType = {
  value: string;
  label: string;
}[];

const acoesField = (item: Pluviometro, onStatusChange: () => Promise<void>) => (
  <ActionButtons item={item} onStatusChange={onStatusChange} />
);

const statusesFilter: StatusesType = [
  { value: '', label: 'Todos' },
  { value: 'active', label: 'Apenas ativos' },
  { value: 'inactive', label: 'Apenas inativos' },
];

const propriedadeTodas: Pick<Propriedade, 'id' | 'descricao'> = {
  id: -1,
  descricao: 'Todas',
};

const PluviometroPage = () => {
  const currentPropriedade = useLocalStore('currentPropriedade');
  const navigate = useNavigate();
  const [propriedadeFilter, setPropriedadeFilter] = useState<number>(
    currentPropriedade.value?.id ?? -1,
  );
  const [statusFilter, setStatusFilter] = useState('active');
  const [descricaoFilter, setDescricaoFilter] = useState('');
  const permissoes = PermissoesService;

  const { data: propriedades } = useAsyncFetch(
    {
      callback: () => PropriedadesService.all(),
      errorMessage: 'Falha ao carregar propriedades.',
      withLoader: true,
    },
    [],
  );

  const propriedadeOptions = useMemo(() => {
    if (!propriedades) return [];

    return [propriedadeTodas, ...propriedades];
  }, [propriedades]);

  const { data: pluviometros, refetch } = useAsyncFetch(
    {
      callback: async () => {
        if (!propriedades) return;

        const params: FilterPluviometrosType = {};

        if (propriedadeFilter && propriedadeFilter !== -1) {
          params.propriedade = propriedadeFilter;
        }

        if (statusFilter) {
          params.status = statusFilter;
        }

        return convertPluviometrosToList(
          await PluviometrosService.list(params),
          propriedades,
        );
      },
      errorMessage: 'Falha ao buscar os pluviômetros.',
      withLoader: true,
    },
    [propriedades],
  );

  const filteredPluviometros = useMemo(() => {
    if (!pluviometros) return [];

    return pluviometros.filter(
      (pluv) =>
        pluv.descricao?.toLowerCase().includes(descricaoFilter.toLowerCase()),
    );
  }, [pluviometros, descricaoFilter]);

  const filter = () => {
    refetch();
  };

  return (
    <div className="container w-full gerenciador-dados-create-edit-container">
      <div className="flex flex-wrap mt-6 mx-3">
        <div className="text-3xl font-normal">Pluviômetros</div>
        {permissoes.pluviometro.manage && (
          <Button
            className="button-secondary ml-auto"
            label="+ Novo"
            severity="success"
            onClick={() => navigate('create')}
          />
        )}
      </div>
      <div>
        <Panel className="mt-4 mx-3">
          <div className="row">
            <div className="flex flex-wrap mt-2">
              <div className="col-12 md:col-4">
                <label className="mx-2">Propriedade:</label>
                <Dropdown
                  value={propriedadeFilter}
                  onChange={(e) => setPropriedadeFilter(e.target.value)}
                  options={propriedadeOptions}
                  optionLabel="descricao"
                  optionValue="id"
                  placeholder="Propriedade"
                  className="mx-2 w-full"
                />
              </div>
              <div className="col-12 md:col-4">
                <label className="mx-2">Status:</label>
                <Dropdown
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  options={statusesFilter}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Status"
                  className="mx-2 w-full"
                />
              </div>
              <div className="col-12 md:col-4">
                <label className="mx-2">Descrição:</label>
                <InputText
                  value={descricaoFilter}
                  onChange={(e) => setDescricaoFilter(e.target.value)}
                  placeholder="Filtrar por descrição"
                  className="mx-2 w-full"
                />
              </div>
            </div>
            <div className="flex justify-content-end">
              <Button
                label="Filtrar"
                className="button-primary"
                onClick={filter}
              />
            </div>
          </div>
          <div className="col-12 m-auto px-4 py-0">
            <ConfirmDialog />

            <DataTable
              value={filteredPluviometros}
              emptyMessage="Nenhum pluviômetro encontrado."
              paginator
              rows={15}
              className="mt-5 p-0 data-table p-datatable-striped"
            >
              <Column
                {...defaultColumnProps('left')}
                field="descricao"
                header="Descrição"
                className="col-4"
              />
              <Column
                {...defaultColumnProps('left')}
                field="propriedade.descricao"
                header="Propriedade"
                className="col-4"
              />
              <Column
                {...defaultColumnProps('left')}
                field="status"
                header="Ativo"
                className="col-4"
              />
              {permissoes.pluviometro.manage && (
                <Column
                  body={(item) => acoesField(item, refetch)}
                  header=" "
                  className="col-1"
                  align="right"
                />
              )}
            </DataTable>
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default PluviometroPage;
